/**
 * Context rail (4th column). Ported from the storybook `final/_helpdesk.tsx`
 * `ContextRail` / `IdentityBlock` / `RailSection` styling, cut down to the two
 * things the backend actually has: the contact's identity (name / email /
 * channel) and the session's live micro-instructions ("steers"). Everything
 * commerce-y in the storybook rail (LTV / CSAT / orders / refunds / history /
 * cross-channel identities / activity) is fiction and omitted per the
 * inbox-backend-gaps doc.
 */
import * as React from "react";
import { Button, Input, cn, toast } from "@cometchat-ai/ui-dashboard";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ChevronRight, Trash2 } from "../components/icons";
import { formatApiError } from "../lib/format-error";
import {
  contactSteerKeys,
  createContactSteer,
  createSteer,
  deleteContactSteer,
  deleteSteer,
  listContactSteers,
  listSteers,
  steerKeys,
  type SessionSteer,
} from "../handoff/api";
import { HdAvatar } from "./helpers";

/** A steer row tagged with where it lives — session (this chat) or contact
 *  (standing, follows the customer across conversations). */
type SteerScope = "session" | "contact";
type ScopedSteer = SessionSteer & { scope: SteerScope };

function RailSection({
  title,
  count,
  defaultOpen = true,
  children,
}: {
  title: string;
  count?: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <section className="px-4.5 py-1.5 first:pt-3 last:pb-3">
      <div className="rounded-lg border border-border bg-card">
        <div className="flex items-center gap-2 px-3 py-2.5">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
          >
            <h3 className="text-2sm font-medium text-muted-foreground">{title}</h3>
            {count != null ? (
              <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-muted px-1.5 text-xxs tabular-nums text-muted-foreground">
                {count}
              </span>
            ) : null}
          </button>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? `Collapse ${title}` : `Expand ${title}`}
            className="inline-flex shrink-0 items-center justify-center rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <ChevronRight
              className={cn("h-3 w-3 rotate-90 transition-transform", open && "-rotate-90")}
            />
          </button>
        </div>
        {open ? <div className="px-3 pb-3">{children}</div> : null}
      </div>
    </section>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2.5 first:pt-0 last:pb-0">
      <dt className="shrink-0 text-[color:var(--color-fg-subtle)]">{label}</dt>
      <dd className="min-w-0 truncate text-right font-medium text-foreground">{value}</dd>
    </div>
  );
}

function IdentityBlock({
  name,
  email,
  channel,
}: {
  name: string;
  email: string | null;
  channel: string;
}) {
  const { t } = useTranslation("workspace");
  return (
    <RailSection title={t("helpdesk.rail.contact")}>
      <div className="pt-1">
        <div className="flex items-center gap-3">
          <HdAvatar name={name} size={44} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-2sm font-medium text-foreground">{name}</div>
            {email ? <div className="truncate text-xs text-muted-foreground">{email}</div> : null}
          </div>
        </div>
        <dl className="-mx-3 mt-3 divide-y divide-dashed divide-border text-xs">
          <Meta label={t("helpdesk.rail.channel")} value={channel} />
          <Meta label={t("helpdesk.rail.email")} value={email ?? "—"} />
        </dl>
      </div>
    </RailSection>
  );
}

function SteerRow({
  steer,
  sessionId,
  contactId,
  slug,
}: {
  steer: ScopedSteer;
  sessionId: string;
  contactId: string | null;
  slug: string;
}) {
  const { t } = useTranslation("workspace");
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () =>
      steer.scope === "contact" && contactId
        ? deleteContactSteer(contactId, steer.id)
        : deleteSteer(sessionId, steer.id),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey:
          steer.scope === "contact" && contactId
            ? contactSteerKeys.list(slug, contactId)
            : steerKeys.list(slug, sessionId),
      }),
    onError: (e) =>
      toast.error(t("helpdesk.steers.removeFailed"), { description: formatApiError(e) }),
  });
  return (
    <li className="flex items-start gap-2 rounded-md border border-border px-2.5 py-2">
      <span className="min-w-0 flex-1 text-xs text-foreground">
        {steer.text}
        <span
          className="ml-1.5 inline-flex rounded bg-muted px-1 py-px align-middle text-[10px] font-medium text-muted-foreground"
          title={
            steer.scope === "contact"
              ? t("helpdesk.steers.scopeCustomerHint")
              : t("helpdesk.steers.scopeChatHint")
          }
        >
          {steer.scope === "contact"
            ? t("helpdesk.steers.scopeCustomer")
            : t("helpdesk.steers.scopeChat")}
        </span>
      </span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={t("helpdesk.steers.remove")}
        disabled={remove.isPending}
        onClick={() => remove.mutate()}
        className="-my-0.5 -mr-1 h-6 w-6 shrink-0 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground [&_svg]:size-3.5"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </li>
  );
}

function SteersSection({
  sessionId,
  contactId,
  slug,
}: {
  sessionId: string;
  contactId: string | null;
  slug: string;
}) {
  const { t } = useTranslation("workspace");
  const queryClient = useQueryClient();
  const [text, setText] = React.useState("");
  // Where a new instruction lands: this chat (session steer) or this customer
  // (standing contact steer that follows them across conversations). Mirrors
  // the original conversations-page SteerPanel scope toggle.
  const [scope, setScope] = React.useState<SteerScope>("session");

  const steersQ = useQuery({
    queryKey: steerKeys.list(slug, sessionId),
    queryFn: () => listSteers(sessionId),
    enabled: !!sessionId,
  });
  const contactSteersQ = useQuery({
    queryKey: contactId ? contactSteerKeys.list(slug, contactId) : ["contact-steers", "none"],
    queryFn: () => listContactSteers(contactId!),
    enabled: !!contactId,
  });
  // Standing (customer) instructions first, then this chat's.
  const steers: ScopedSteer[] = [
    ...(contactSteersQ.data?.items ?? []).map((s) => ({ ...s, scope: "contact" as const })),
    ...(steersQ.data?.items ?? []).map((s) => ({ ...s, scope: "session" as const })),
  ];

  const add = useMutation({
    mutationFn: (value: string) =>
      scope === "contact" && contactId
        ? createContactSteer(contactId, { text: value })
        : createSteer(sessionId, { text: value }),
    onSuccess: () => {
      setText("");
      void queryClient.invalidateQueries({
        queryKey:
          scope === "contact" && contactId
            ? contactSteerKeys.list(slug, contactId)
            : steerKeys.list(slug, sessionId),
      });
    },
    onError: (e) => toast.error(t("helpdesk.steers.addFailed"), { description: formatApiError(e) }),
  });

  const submit = () => {
    const value = text.trim();
    if (!value || add.isPending) return;
    add.mutate(value);
  };

  return (
    <RailSection title={t("helpdesk.steers.title")} count={steers.length}>
      {steers.length === 0 ? (
        <p className="py-1 text-xs text-[color:var(--color-fg-subtle)]">
          {t("helpdesk.steers.empty")}
        </p>
      ) : (
        <ul className="space-y-1.5">
          {steers.map((s) => (
            <SteerRow
              key={`${s.scope}-${s.id}`}
              steer={s}
              sessionId={sessionId}
              contactId={contactId}
              slug={slug}
            />
          ))}
        </ul>
      )}
      {contactId ? (
        <div
          className="mt-2.5 flex overflow-hidden rounded-md border border-border"
          role="radiogroup"
          aria-label={t("helpdesk.steers.scopeLabel")}
        >
          {(["session", "contact"] as const).map((sc) => (
            <button
              key={sc}
              type="button"
              role="radio"
              aria-checked={scope === sc}
              onClick={() => setScope(sc)}
              data-testid={`helpdesk-steer-scope-${sc}`}
              className={cn(
                "flex-1 px-2 py-1 text-[11px] font-medium transition-colors",
                scope === sc
                  ? "bg-foreground text-background"
                  : "bg-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {sc === "session"
                ? t("helpdesk.steers.scopeChat")
                : t("helpdesk.steers.scopeCustomer")}
            </button>
          ))}
        </div>
      ) : null}
      <div className="mt-2 flex items-center gap-1.5">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
          spellCheck={false}
          placeholder={
            scope === "contact" && contactId
              ? t("helpdesk.steers.placeholderContact")
              : t("helpdesk.steers.placeholder")
          }
          data-testid="helpdesk-steer-input"
          className="h-8 flex-1 bg-background text-xs placeholder:text-[color:var(--color-fg-subtle)]"
        />
        <Button
          type="button"
          size="sm"
          disabled={!text.trim() || add.isPending}
          onClick={submit}
          data-testid="helpdesk-steer-add"
          className="h-8 shrink-0 rounded-md text-2sm"
        >
          {t("helpdesk.steers.add")}
        </Button>
      </div>
    </RailSection>
  );
}

export function ContextRail({
  sessionId,
  contactId,
  name,
  email,
  channel,
  slug,
}: {
  sessionId: string;
  /** Enables the "This customer" steer scope; anonymous sessions pass null. */
  contactId: string | null;
  name: string;
  email: string | null;
  channel: string;
  slug: string;
}) {
  const { t } = useTranslation("workspace");
  return (
    <aside className="flex w-80 shrink-0 flex-col border-l border-border bg-[color:var(--color-bg-subtle)]">
      <div className="flex h-12 shrink-0 items-center border-b border-border bg-background px-4.5">
        <span className="text-base font-semibold tracking-[0.01em] text-foreground">
          {t("helpdesk.rail.details")}
        </span>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        <IdentityBlock name={name} email={email} channel={channel} />
        <SteersSection sessionId={sessionId} contactId={contactId} slug={slug} />
      </div>
    </aside>
  );
}
